import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { recoverAccount, clearDeletedAccountInfo } from '../../redux/slices/authSlice';

// ─────────────────────────────────────────────────────────────
// RecoverAccountPage
//
// Route: /recover-account
// Add to your router: <Route path="/recover-account" element={<RecoverAccountPage />} />
//
// Two entry points:
//  1. From the login page when a 403 isDeleted:true is received —
//     Redux state already has deletedAccountInfo.scheduledDeletionAt
//     so we show the countdown prominently.
//  2. Direct URL visit — user manually navigates here.
// ─────────────────────────────────────────────────────────────

const RecoverAccountPage = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { user, recoverLoading, deletedAccountInfo } = useSelector((s) => s.auth);

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState(false);

  // Already logged in → go to profile
  useEffect(() => {
    if (user) navigate('/profile');
  }, [user]);

  const scheduledDate = deletedAccountInfo?.scheduledDeletionAt
    ? new Date(deletedAccountInfo.scheduledDeletionAt).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  const daysLeft = deletedAccountInfo?.scheduledDeletionAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(deletedAccountInfo.scheduledDeletionAt) - new Date()) /
          (1000 * 60 * 60 * 24)
        )
      )
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password');
      return;
    }

    const result = await dispatch(recoverAccount({ email: email.trim().toLowerCase(), password }));

    if (recoverAccount.rejected.match(result)) {
      const payload = result.payload;
      if (payload?.expired) {
        setError('Your recovery window has expired. This account has been permanently deleted.');
      } else {
        setError(payload?.message || 'Recovery failed. Please check your credentials.');
      }
      return;
    }

    // Success — show a brief confirmation then navigate
    setSuccess(true);
    dispatch(clearDeletedAccountInfo());
    setTimeout(() => navigate('/profile'), 2000);
  };

  // ─────────────────────────────────────────────────────────
  return (
    <>
      <style>{recoverStyles}</style>

      <div className="ra-root">
        <div className="ra-card">

          {/* Eyebrow */}
          <span className="ra-eyebrow">Account Recovery</span>
          <h1 className="ra-title">Restore <span className="ra-title-bold">Account</span></h1>
          <div className="ra-rule-wrap"><hr className="ra-hr" /></div>

          {/* Countdown banner — shown when coming from login 403 */}
          {scheduledDate && daysLeft !== null && (
            <div className="ra-countdown-banner">
              <p className="ra-countdown-label">Recovery window</p>
              <p className="ra-countdown-days">
                {daysLeft > 0
                  ? <><strong>{daysLeft}</strong> day{daysLeft !== 1 ? 's' : ''} remaining</>
                  : 'Expires today'}
              </p>
              <p className="ra-countdown-date">
                Permanent deletion scheduled for <strong>{scheduledDate}</strong>
              </p>
            </div>
          )}

          {/* Explanation */}
          <p className="ra-desc">
            Enter the email and password you used before deleting your account.
            Verifying your credentials will restore access immediately.
          </p>

          {/* Success state */}
          {success ? (
            <div className="ra-success-block">
              <p className="ra-success-icon">✓</p>
              <p className="ra-success-msg">Account restored. Redirecting…</p>
            </div>
          ) : (
            <form className="ra-form" onSubmit={handleSubmit}>

              <div className="ra-field">
                <label className="ra-label">Email</label>
                <input
                  className="ra-input"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="your@email.com"
                  autoComplete="email"
                  disabled={recoverLoading}
                  autoFocus
                />
              </div>

              <div className="ra-field">
                <label className="ra-label">Password</label>
                <input
                  className="ra-input"
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Your account password"
                  autoComplete="current-password"
                  disabled={recoverLoading}
                />
              </div>

              {error && <p className="ra-error">{error}</p>}

              <button
                type="submit"
                className="ra-btn-submit"
                disabled={recoverLoading}
              >
                {recoverLoading ? 'Restoring…' : 'Restore My Account'}
              </button>

            </form>
          )}

          <div className="ra-rule-wrap" style={{ marginTop: 32 }}><hr className="ra-hr ra-hr--faint" /></div>

          {/* Footer links */}
          <div className="ra-footer-links">
            <Link to="/login" className="ra-link">Back to Login</Link>
            <span className="ra-link-sep">·</span>
            <a href="mailto:support@veshheon.in" className="ra-link">Contact Support</a>
          </div>

        </div>
      </div>
    </>
  );
};

/* ── STYLES ── */
const recoverStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600;700&display=swap');

  .ra-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    box-sizing: border-box;
  }

  .ra-card {
    background: #fff;
    width: 100%;
    max-width: 440px;
    padding: 48px 44px 40px;
    box-sizing: border-box;
  }

  /* ── Eyebrow + Title ── */
  .ra-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400; letter-spacing: 0.45em;
    text-transform: uppercase; color: black; margin-bottom: 10px;
  }
  .ra-eyebrow::before {
    content: ''; width: 18px; height: 1px;
    background: rgba(0,0,0,0.15); display: block; flex-shrink: 0;
  }
  .ra-title {
    font-size: clamp(24px, 4vw, 34px); font-weight: 200;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: #000; line-height: 1; margin: 0 0 20px;
  }
  .ra-title-bold { font-weight: 600; }

  /* ── Rule ── */
  .ra-rule-wrap { margin-bottom: 0; }
  .ra-hr { border: none; border-top: 1px solid black; margin: 0 0 24px; }
  .ra-hr--faint { border-top-color: rgba(0,0,0,0.1); margin: 0; }

  /* ── Countdown banner ── */
  .ra-countdown-banner {
    background: #000;
    color: #fff;
    padding: 18px 20px;
    margin-bottom: 24px;
  }
  .ra-countdown-label {
    font-size: 7px; font-weight: 600; letter-spacing: 0.45em;
    text-transform: uppercase; color: rgba(255,255,255,0.5);
    margin: 0 0 6px;
  }
  .ra-countdown-days {
    font-size: 20px; font-weight: 200; letter-spacing: 0.06em;
    color: #fff; margin: 0 0 4px;
  }
  .ra-countdown-days strong { font-weight: 700; }
  .ra-countdown-date {
    font-size: 9px; font-weight: 300; letter-spacing: 0.08em;
    color: rgba(255,255,255,0.55); margin: 0;
  }
  .ra-countdown-date strong { color: rgba(255,255,255,0.8); font-weight: 500; }

  /* ── Description ── */
  .ra-desc {
    font-size: 12px; font-weight: 300; letter-spacing: 0.04em;
    color: rgba(0,0,0,0.55); line-height: 1.7;
    margin: 0 0 28px;
  }

  /* ── Form ── */
  .ra-form { display: flex; flex-direction: column; gap: 20px; }
  .ra-field { display: flex; flex-direction: column; gap: 8px; }
  .ra-label {
    font-size: 8px; font-weight: 500; letter-spacing: 0.45em;
    text-transform: uppercase; color: black;
  }
  .ra-input {
    background: transparent; border: none;
    border-bottom: 1px solid black; outline: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 13px; font-weight: 300; letter-spacing: 0.06em; color: #000;
    padding: 8px 0; border-radius: 0;
    -webkit-appearance: none; width: 100%;
    transition: border-color 0.2s;
  }
  .ra-input::placeholder { color: rgba(0,0,0,0.3); }
  .ra-input:focus { border-bottom-color: #000; }
  .ra-input:disabled { opacity: 0.5; }

  /* ── Error ── */
  .ra-error {
    font-size: 9px; font-weight: 400; letter-spacing: 0.15em;
    text-transform: uppercase; color: #c00; margin: 0;
  }

  /* ── Submit ── */
  .ra-btn-submit {
    background: #000; color: #fff; border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 9px; font-weight: 600; letter-spacing: 0.4em;
    text-transform: uppercase; padding: 16px;
    cursor: pointer; width: 100%; margin-top: 4px;
    transition: background 0.2s;
  }
  .ra-btn-submit:hover:not(:disabled) { background: #222; }
  .ra-btn-submit:disabled { background: rgba(0,0,0,0.3); cursor: not-allowed; }

  /* ── Success ── */
  .ra-success-block {
    display: flex; flex-direction: column;
    align-items: center; gap: 12px;
    padding: 32px 0;
  }
  .ra-success-icon {
    font-size: 28px; color: #000; margin: 0; line-height: 1;
  }
  .ra-success-msg {
    font-size: 9px; font-weight: 500; letter-spacing: 0.35em;
    text-transform: uppercase; color: #000; margin: 0;
  }

  /* ── Footer links ── */
  .ra-footer-links {
    display: flex; align-items: center; gap: 10px;
    margin-top: 20px;
  }
  .ra-link {
    font-size: 8px; font-weight: 500; letter-spacing: 0.3em;
    text-transform: uppercase; color: rgba(0,0,0,0.4);
    text-decoration: none;
    border-bottom: 1px solid rgba(0,0,0,0.15);
    padding-bottom: 1px;
    transition: color 0.2s, border-color 0.2s;
  }
  .ra-link:hover { color: #000; border-color: #000; }
  .ra-link-sep {
    font-size: 8px; color: rgba(0,0,0,0.2);
  }

  /* ── Responsive ── */
  @media (max-width: 480px) {
    .ra-card { padding: 36px 24px 32px; }
  }
`;

export default RecoverAccountPage;
